import { AfterViewInit, ChangeDetectorRef, Component, computed, EventEmitter, inject, Input, Output, signal } from '@angular/core';
import { NgbActiveOffcanvas } from '@ng-bootstrap/ng-bootstrap';
import { DataViewConfigService } from '@services/dataview-config.service';
import { BaseComponent } from '@shared/components/base-component';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { forkJoin } from 'rxjs';
import { UserService } from '@services/user.service';
import { Column } from '@shared/components/data-view/data-view-types';
import { flatMap, sum } from 'lodash';

@Component({
  selector: 'app-data-view-edit-columns',
  templateUrl: './data-view-edit-columns.component.html',
  styleUrls: ['./data-view-edit-columns.component.scss'],
  standalone: false,
})
export class DataViewEditColumnsComponent extends BaseComponent implements AfterViewInit {
  private readonly cdr = inject(ChangeDetectorRef);

  @Input() pageName: string;
  @Output() triggerColumnsUpdate = new EventEmitter<void>();
  @Output() triggerColumnAdded = new EventEmitter<void>();
  columnCategories: Column.DataViewColumnGalleryCategory[] = [];

  appliedColumns = signal<Column.AppliedDataViewColumn[]>([]);
  favouriteColumnIds = signal<string[]>([]);
  favouriteColumns = computed(() => {
    const favouriteColumnIds = this.favouriteColumnIds();
    const availableColumns = this.availableColumns();
    return flatMap(Object.values(availableColumns)).filter(column => favouriteColumnIds.includes(column.id));
  });
  favouriteColumnsFilteredBySearch = computed(() => this.getFilteredColumns(this.searchQuery(), this.favouriteColumns()));

  searchQuery = signal<string | null>(null);
  availableColumns = signal<Record<string, Column.DataViewColumnGalleryColumn[]>>({});
  availableColumnsFilteredBySearch = computed(() => this.getFilteredAvailableColumns(this.searchQuery(), this.availableColumns()));
  filteredColumnsCount = computed(() => sum(Object.values(this.availableColumnsFilteredBySearch()).map(columns => columns.length)));

  constructor(
    protected activeOffcanvas: NgbActiveOffcanvas,
    private dataViewConfigService: DataViewConfigService,
    private userService: UserService,
  ) {
    super();
  }

  ngAfterViewInit() {
    if (!this.pageName) return;
    this.subscribe(
      forkJoin({
        columnCategories: this.dataViewConfigService.getColumnCategories(this.pageName, this.userService.getCurrentUser()),
        columns: this.dataViewConfigService.getAvailableColumns(this.pageName, this.userService.getCurrentUser()),
      }),
      result => this.setColumns(result.columnCategories, result.columns),
    );
  }

  setColumns(columnCategories: Column.DataViewColumnGalleryCategory[], columns: Column.DataViewColumn[]) {
    this.columnCategories = columnCategories;
    for (const category of this.columnCategories) {
      if (!this.availableColumns()[category.id]) this.availableColumns()[category.id] = [];
    }
    for (const column of columns) {
      if (!column.categoryId || !this.availableColumns()[column.categoryId]) {
        continue;
      }
      if (this.appliedColumns().findIndex(ac => ac.id === column.id) !== -1) {
        (column as Column.DataViewColumnGalleryColumn).isAdded = true;
      }
      this.availableColumns()[column.categoryId].push(column);
    }
    this.availableColumns.set({ ...this.availableColumns() });
    this.cdr.markForCheck();
  }

  onSubmit(appliedColumns: Column.DataViewColumn[]) {
    this.activeOffcanvas.dismiss({
      ...appliedColumns,
    });
  }

  columnAdded(column: Column.DataViewColumnGalleryColumn, atIndex?: number) {
    column.isAdded = true;
    (column as Column.AppliedDataViewColumn).width = 'auto'; // Need to pass a reference instead of a copy
    if (atIndex !== undefined) {
      this.appliedColumns().splice(atIndex, 0, column);
    } else {
      this.appliedColumns().push(column);
    }
    this.availableColumns.set({ ...this.availableColumns() });
    this.appliedColumns.set(this.appliedColumns());
    this.triggerColumnsUpdate.next();
    this.triggerColumnAdded.next();
  }

  columnRemoved(columnId: number) {
    const removedCol = this.appliedColumns().splice(columnId, 1);
    for (const colCategory of Object.keys(this.availableColumns())) {
      for (const col of this.availableColumns()[colCategory]) {
        if (col.id === removedCol[0].id) {
          col.isAdded = false;
          break;
        }
      }
    }
    this.availableColumns.set({ ...this.availableColumns() });
    this.appliedColumns.set(this.appliedColumns());
    this.triggerColumnsUpdate.next();
  }

  columnToggleFavourite(column: Column.DataViewColumnGalleryColumn) {
    const favouriteIndex = this.favouriteColumnIds().findIndex(favColId => favColId === column.id);
    if (favouriteIndex === -1) {
      this.favouriteColumnIds.set([...this.favouriteColumnIds(), column.id]);
    } else {
      this.favouriteColumnIds().splice(favouriteIndex, 1);
      this.favouriteColumnIds.set([...this.favouriteColumnIds()]); // Trigger setter
    }
    this.triggerColumnsUpdate.next();
  }

  columnDroppedFavourites(event: CdkDragDrop<Column.DataViewColumn[], Column.DataViewColumn[]>) {
    if (event.previousContainer.id === 'selected-list') {
      this.columnRemoved(event.previousIndex);
    }
  }

  columnDroppedCategories(event: CdkDragDrop<Column.DataViewColumn[], Column.DataViewColumn[]>) {
    if (event.previousContainer.id === 'selected-list') {
      this.columnRemoved(event.previousIndex);
    }
  }

  columnDroppedSelected(event: CdkDragDrop<Column.DataViewColumn[], Column.DataViewColumn[]>) {
    if (event.container === event.previousContainer) {
      moveItemInArray(this.appliedColumns(), event.previousIndex, event.currentIndex);
      this.appliedColumns.set(this.appliedColumns());
      this.triggerColumnsUpdate.next();
    } else if (event.previousContainer.id.startsWith('categories-list')) {
      const categoryId = event.previousContainer.id.split('categories-list-')[1];
      this.columnAdded(this.availableColumnsFilteredBySearch()[categoryId][event.previousIndex], event.currentIndex);
    } else {
      this.columnAdded(this.favouriteColumnsFilteredBySearch()[event.previousIndex], event.currentIndex);
    }
  }

  private getFilteredColumns(searchQuery: string | null, columns: Column.DataViewColumnGalleryColumn[]) {
    return columns.filter(
      col => !col.isAdded && (!searchQuery || (col.display ?? col.field).toLowerCase().includes(searchQuery.toLowerCase())),
    );
  }

  private getFilteredAvailableColumns(searchQuery: string | null, availableColumns: Record<string, Column.DataViewColumnGalleryColumn[]>) {
    const filteredColumns: Record<string, Column.DataViewColumnGalleryColumn[]> = {};
    for (const category of Object.keys(availableColumns)) {
      filteredColumns[category] = this.getFilteredColumns(searchQuery, availableColumns[category]);
    }
    return filteredColumns;
  }
}
